// import { Navigate, Route } from 'react-router-dom';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  EntityKindPicker,
  EntityTypePicker,
  UserListPicker,
  EntityNamespacePicker,
  EntityOwnerPicker,
  EntityLifecyclePicker,
  EntityTagPicker,
} from '@backstage/plugin-catalog-react';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { CustomCatalogIndexPage } from './components/catalog/CustomCatalogIndexPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  AutoLogout,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { AndroidReleasesPage } from './components/android-releases';
import { BrowserCloseLogout } from './components/BrowserCloseLogout';
import { HomePage } from './components/home';
import { ReportsPage } from './components/reports';
import { MyRequestsPage } from './components/requests';
import { MobileBuildsPage } from './components/mobile-builds';
import { VideosPage } from './components/videos';
import { SnykIgnoresPage } from './components/snyk-ignores';
import { EcoDashboardPage } from './components/eco-dashboard';
import { UsagePage } from './components/usage';
import { DevopsNewsPage } from './components/devops-news';
import { ReleasesPage } from './components/releases';
import { TeamsPage } from './components/teams';
import { ArgoValuesEditorExtension } from './components/argo-values';
import { RoutePrefixFieldExtension } from './components/routes';
import { IacRepositoryFieldExtension, IacSecretWiringFieldExtension, IacValueFieldExtension, IacValueNameFieldExtension } from './components/iac';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { githubAuthApiRef } from '@backstage/core-plugin-api';

import LightIcon from '@material-ui/icons/Brightness7';
import DarkIcon from '@material-ui/icons/Brightness4';
import { createUnifiedTheme, palettes,UnifiedThemeProvider} from '@backstage/theme';
import { deunaLightTheme,deunaDarkTheme } from './theme';

// crea temas light y dark 
const light = createUnifiedTheme({ palette: palettes.light });
const dark = createUnifiedTheme({ palette: palettes.dark });

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
      viewTechDoc: false,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'github-auth-provider',
          title: 'GitHub',
          message: 'Sign in using GitHub',
          apiRef: githubAuthApiRef,
        }}
      />
    ),
  },
  themes: [
    {
      id: 'light',
      title: 'Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={light}>{children}</UnifiedThemeProvider>
      ),
    },
    {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dark}>{children}</UnifiedThemeProvider>
      ),
    },
    {
      id: 'deunaLightTheme',
      title: 'Deuna Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={deunaLightTheme}>{children}</UnifiedThemeProvider>
      ),
    },
    {
      id: 'deunaDarkTheme',
      title: 'Deuna Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={deunaDarkTheme}>{children}</UnifiedThemeProvider>
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    {/* <Route path="/" element={<Navigate to="catalog" />} /> */}
    <Route path="/" element={<HomePage />} />
    <Route
      path="/catalog"
      element={
        <CustomCatalogIndexPage>
          <CatalogIndexPage
            initiallySelectedFilter="all"
            filters={
              <>
                <EntityKindPicker allowedKinds={['component', 'api', 'group', 'user', 'system', 'domain', 'resource']} />
                <EntityTypePicker />
                <UserListPicker />
                <EntityNamespacePicker />
                <EntityOwnerPicker />
                <EntityLifecyclePicker />
                <EntityTagPicker />
              </>
            }
          />
        </CustomCatalogIndexPage>
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          headerOptions={{ title: 'Autoservicio', subtitle: 'Pide repos, cambia la configuración de un servicio o da de alta a una persona' }}
          groups={[
            { title: 'Repositorios nuevos', filter: e => e.spec?.type === 'service' },
            { title: 'Cambios en servicios y repos', filter: e => e.spec?.type === 'self-service' },
            { title: 'Personas', filter: e => e.spec?.type === 'onboarding' },
          ]}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <ArgoValuesEditorExtension />
        <RoutePrefixFieldExtension />
        <IacRepositoryFieldExtension />
        <IacValueNameFieldExtension />
        <IacSecretWiringFieldExtension />
        <IacValueFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/android-releases" element={<AndroidReleasesPage />} />
    <Route path="/reports" element={<ReportsPage />} />
    <Route path="/my-requests" element={<MyRequestsPage />} />
    <Route path="/mobile-builds" element={<MobileBuildsPage />} />
    <Route path="/videos" element={<VideosPage />} />
    <Route path="/snyk-ignores" element={<SnykIgnoresPage />} />
    <Route path="/eco-dashboard" element={<EcoDashboardPage />} />
    <Route path="/usage" element={<UsagePage />} />
    <Route path="/devops-news" element={<DevopsNewsPage />} />
    <Route path="/releases" element={<ReleasesPage />} />
    <Route path="/teams-roles" element={<TeamsPage />} />
    <Route path="/tech-radar" element={<TechRadarPage width={1500} height={800} />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <BrowserCloseLogout />
    <SignalsDisplay />
    <AppRouter>
      <AutoLogout
        idleTimeoutMinutes={5}
        promptBeforeIdleSeconds={30}
        useWorkerTimers={false}
      />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
